// src/pages/ForgotPasswordPage.jsx
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../utils/firebase";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");
  const navigate = useNavigate();

async function handleSubmit(e) {
  e.preventDefault();
  setError("");

  if (!email.trim()) {
    setError("Please enter your email address.");
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    setError("Please enter a valid email address.");
    return;
  }

  setStatus("sending");

  try {
    // Directly send reset email — Firebase will throw if email not found
    await sendPasswordResetEmail(auth, email.trim());
    setStatus("success");

  } catch (err) {
    console.error("Password reset error:", err.code, err.message);

    if (
      err.code === "auth/user-not-found" ||
      err.code === "auth/invalid-email" ||
      err.code === "auth/invalid-credential"
    ) {
      setError("No account found with this email address. Please check or create a new account.");
    } else if (err.code === "auth/too-many-requests") {
      setError("Too many attempts. Please wait a few minutes and try again.");
    } else {
      // For any other error, still show success to avoid exposing account info
      // The email was likely sent successfully
      setStatus("success");
    }
    if (status !== "success") setStatus("error");
  }
}

  return (
    <div style={{ minHeight: "100vh", background: "var(--gray-50)", display: "flex", flexDirection: "column" }}>

      {/* NAV */}
      <nav style={{ borderBottom: "1px solid var(--gray-200)", padding: "0 32px", height: 60, display: "flex", alignItems: "center", background: "#fff" }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
          <div style={{ width: 30, height: 30, background: "var(--blue)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#fff", fontSize: 14 }}>📄</span>
          </div>
          <span style={{ fontWeight: 700, fontSize: 16, color: "var(--gray-900)" }}>
            ResumeScore<span style={{ color: "var(--blue)" }}>AI</span>
          </span>
        </Link>
      </nav>

      {/* CONTENT */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 16px" }}>
        <div style={{ width: "100%", maxWidth: 420 }}>

          {status === "success" ? (
            /* ── Success state ── */
            <div className="card" style={{ padding: "40px 32px", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📧</div>
              <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 10, letterSpacing: "-0.02em" }}>
                Check your email
              </h1>
              <p style={{ fontSize: 14, color: "var(--gray-500)", marginBottom: 8, lineHeight: 1.7 }}>
                We've sent a password reset link to:
              </p>
              <div style={{ background: "var(--blue-light)", color: "var(--blue)", fontSize: 14, fontWeight: 600, padding: "10px 16px", borderRadius: 8, marginBottom: 16 }}>
                {email}
              </div>
              <p style={{ fontSize: 13, color: "var(--gray-400)", marginBottom: 28, lineHeight: 1.7 }}>
                Click the link in the email to reset your password.
                The link expires in <strong>1 hour</strong>.
                Please also check your spam folder.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button
                  className="btn btn-primary btn-full btn-lg"
                  onClick={() => navigate("/login")}
                >
                  Back to Login
                </button>
                <button
                  className="btn btn-outline btn-full"
                  onClick={() => { setStatus("idle"); setEmail(""); setError(""); }}
                >
                  Try a different email
                </button>
              </div>
            </div>
          ) : (
            /* ── Form state ── */
            <div className="card" style={{ padding: "36px 32px" }}>
              <div style={{ textAlign: "center", marginBottom: 28 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔑</div>
                <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, letterSpacing: "-0.02em" }}>
                  Forgot your password?
                </h1>
                <p style={{ fontSize: 14, color: "var(--gray-500)", lineHeight: 1.6 }}>
                  Enter your registered email address and we'll send you a password reset link.
                </p>
              </div>

              {/* Error message */}
              {error && (
                <div style={{ background: "#FCEBEB", color: "#A32D2D", fontSize: 13, padding: "12px 14px", borderRadius: 8, marginBottom: 20, border: "1px solid #f0a0a0", lineHeight: 1.6 }}>
                  {error}
                  {/* Show signup link if email not found */}
                  {error.includes("No account found") && (
                    <div style={{ marginTop: 8 }}>
                      <Link to="/signup" style={{ color: "#A32D2D", fontWeight: 600, textDecoration: "underline" }}>
                        Create a new account →
                      </Link>
                    </div>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Email address</label>
                  <input
                    className="form-input"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      if (error) setError(""); // Clear error on type
                    }}
                    required
                    autoFocus
                    disabled={status === "sending"}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-full btn-lg"
                  disabled={status === "sending"}
                  style={{ marginTop: 8 }}
                >
                  {status === "sending"
                    ? <><span className="spinner" style={{ width: 16, height: 16 }} /> Checking email...</>
                    : "Send Reset Link"
                  }
                </button>
              </form>

              <div style={{ display: "flex", justifyContent: "center", gap: 20, marginTop: 20 }}>
                <Link to="/login" style={{ fontSize: 14, color: "var(--blue)", fontWeight: 500 }}>
                  ← Back to Login
                </Link>
                <Link to="/signup" style={{ fontSize: 14, color: "var(--blue)", fontWeight: 500 }}>
                  Create account
                </Link>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}